import { useState, useEffect } from "react";
import {
  Stethoscope,
  CurrencyCircleDollar,
  Door,
  Warning,
  Ticket,
  CaretUp,
  CaretDown,
  Info,
  DoorOpen,
  VideoCamera,
} from "@phosphor-icons/react";
import { getRateStep } from "../../../../utils/appointmentFormRules";
import {
  HEBREW_DAYS_FULL,
  formatHebrewDate,
  formatTime,
} from "../../../../utils/dateUtils";
import RoomBlockedPanel from "./RoomBlockedPanel";
import UnassignedPatientPanel from "./UnassignedPatientPanel";

// Sentinel for "use the base room" in the per-meeting room select
const ROOM_DEFAULT = "__default__";

const RATE_SOURCE_LABEL = {
  patient: "תעריף אישי של המטופל",
  treatmentType: "תעריף סוג הטיפול",
  clinic: "תעריף ברירת מחדל של הקליניקה",
  manual: "תעריף שהוזן ידנית",
  punchCard: "מנוקב מכרטיסיה",
};

/**
 * Step 3 — treatment type, payment (finance-permission only, with punch card),
 * room (only when the clinic has active rooms). Zero-amount and no-room
 * confirmations are inline gates.
 */
function DetailsStep({ form }) {
  const {
    formData,
    updateField,
    stepErrors,
    treatmentTypes,
    canAccessFinance,
    rateSource,
    durationSource,
    rateDisabled,
    needsAmountGate,
    zeroAmountConfirmed,
    setZeroAmountConfirmed,
    showPunchOption,
    usePunchCard,
    handlePunchToggle,
    remainingPunches,
    punchAmountForCard,
    roomsEnabled,
    accessibleRooms,
    roomConflicts,
    roomAvailabilityError,
    roomCheckPending,
    noRoomConfirmed,
    setNoRoomConfirmed,
    seriesPreview,
    seriesOverrides,
    setSeriesOverrides,
    seriesRoomConflicts,
    seriesRoomCheckPending,
    seriesEditRoomWarnings,
    isAdmin,
    hasUnassignedTherapist,
    seriesUnassignedIdx = [],
  } = form;

  // Series conflicts still needing action (not admin-overridden)
  const unresolvedSeriesConflicts = seriesRoomConflicts.filter(
    (c) => !(isAdmin && seriesOverrides[c.idx]?.override === true)
  );

  const [showPerMeetingRooms, setShowPerMeetingRooms] = useState(false);

  // A conflicting occurrence must be visible so the user can fix its room.
  useEffect(() => {
    if (seriesRoomConflicts.length > 0) setShowPerMeetingRooms(true);
  }, [seriesRoomConflicts.length]);

  const adjustAmount = (direction) => {
    const current = parseFloat(formData.paymentAmount);
    const base = Number.isFinite(current) ? current : 0;
    const step = getRateStep(formData.paymentAmount);
    const next = Math.max(0, Math.round((base + direction * step) * 100) / 100);
    updateField("paymentAmount", String(next));
  };

  const showNoRoomGate =
    !form.isEditing && roomsEnabled && accessibleRooms.length > 0 && !formData.roomId;

  return (
    <div className="apt-wiz-step-panel" key="step-details">
      {/* E6: patient not assigned to the target therapist — assign inline
          (multi-therapist clinics), acknowledge (admins / manageOthers) or
          hard block. Stays mounted after acknowledgement to show the note. */}
      {hasUnassignedTherapist && <UnassignedPatientPanel form={form} />}

      <div className="apt-wiz-form-group">
        <label>
          <Stethoscope size={13} weight="fill" /> סוג טיפול
        </label>
        {treatmentTypes.length === 0 ? (
          <div className="apt-wiz-info-note">
            <Info size={12} weight="fill" />
            <span>
              טרם הוגדרו סוגי טיפול — ניתן להגדיר אותם בהגדרות הקליניקה,
              בלשונית ״התאמה אישית״
            </span>
          </div>
        ) : (
          <select
            value={formData.treatmentTypeId}
            onChange={(e) => updateField("treatmentTypeId", e.target.value)}
          >
            <option value="">ללא סוג טיפול</option>
            {treatmentTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.duration > 0 ? ` · ${t.duration} דק׳` : ""}
              </option>
            ))}
          </select>
        )}
        {durationSource === "treatmentType" &&
          formData.treatmentTypeId &&
          !formData.isBlockedTime && (
            <div className="apt-wiz-rate-hint">
              משך הפגישה עודכן ל-{formData.duration} דקות לפי סוג הטיפול
            </div>
          )}
      </div>

      {form.onlineMeetingsEnabled && !formData.isBlockedTime && (
        <label
          className={`apt-wiz-toggle-card ${formData.isOnlineMeeting ? "on" : ""}`}
        >
          <input
            type="checkbox"
            checked={formData.isOnlineMeeting === true}
            onChange={(e) => updateField("isOnlineMeeting", e.target.checked)}
          />
          <span className="apt-wiz-toggle-check" />
          <span className="apt-wiz-toggle-text">
            <span className="apt-wiz-toggle-title">
              <VideoCamera size={14} weight="fill" /> פגישת וידאו אונליין
            </span>
            <span className="apt-wiz-toggle-sub">
              קישור להצטרפות לשיחת הווידאו יישלח למטופל יחד עם אישור ההגעה
            </span>
          </span>
        </label>
      )}

      {canAccessFinance && (
        <div className="apt-wiz-form-group">
          <label>
            <CurrencyCircleDollar size={13} weight="fill" /> תעריף (₪)
          </label>
          <div className="apt-wiz-amount-box">
            <button
              type="button"
              className="apt-wiz-amount-btn"
              onClick={() => adjustAmount(-1)}
              disabled={rateDisabled}
              aria-label="הפחתת תעריף"
            >
              <CaretDown size={14} />
            </button>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              value={formData.paymentAmount}
              onChange={(e) => updateField("paymentAmount", e.target.value)}
              disabled={rateDisabled}
              className={stepErrors.paymentAmount ? "has-error" : ""}
              dir="ltr"
            />
            <button
              type="button"
              className="apt-wiz-amount-btn"
              onClick={() => adjustAmount(1)}
              disabled={rateDisabled}
              aria-label="הגדלת תעריף"
            >
              <CaretUp size={14} />
            </button>
          </div>
          {rateSource && (
            <div className="apt-wiz-rate-hint">{RATE_SOURCE_LABEL[rateSource]}</div>
          )}
          {stepErrors.paymentAmount && (
            <div className="apt-wiz-field-error">{stepErrors.paymentAmount}</div>
          )}
        </div>
      )}

      {showPunchOption && (
        <label className={`apt-wiz-toggle-card ${usePunchCard ? "on" : ""}`}>
          <input
            type="checkbox"
            checked={usePunchCard}
            onChange={(e) => handlePunchToggle(e.target.checked)}
          />
          <span className="apt-wiz-toggle-check" />
          <span className="apt-wiz-toggle-text">
            <span className="apt-wiz-toggle-title">
              <Ticket size={14} weight="fill" /> מתוך כרטיסיה
            </span>
            <span className="apt-wiz-toggle-sub">
              {form.isEditing && form.appointment?.isPunchCard === true
                ? usePunchCard
                  ? "הפגישה מנוקבת מכרטיסיה — ביטול הסימון ישחזר את הניקוב לכרטיסיה"
                  : "הניקוב ישוחזר לכרטיסיה בשמירה"
                : `נותרו ${remainingPunches} ניקובים${
                    formData.isRecurring
                      ? " — המפגשים הראשונים ינוקבו, היתר בתעריף רגיל"
                      : punchAmountForCard === 0
                      ? " — הפגישה תירשם ב-₪0 (שולם מראש)"
                      : ` — ₪${punchAmountForCard} לפגישה`
                  }`}
            </span>
          </span>
        </label>
      )}

      {needsAmountGate && (
        <div className={`apt-wiz-gate-card ${zeroAmountConfirmed ? "confirmed" : ""}`}>
          <div className="apt-wiz-gate-text">
            <Warning size={15} weight="fill" />
            <span>
              לא הוגדר תעריף — הפגישה לא תיכלל במעקב התשלומים ובדוחות ההכנסה.
            </span>
          </div>
          {!zeroAmountConfirmed ? (
            <button
              type="button"
              className="apt-wiz-gate-btn"
              onClick={() => setZeroAmountConfirmed(true)}
            >
              המשך ללא תעריף
            </button>
          ) : (
            <span className="apt-wiz-gate-ok">אושר</span>
          )}
        </div>
      )}

      {roomsEnabled && (
        <div className="apt-wiz-form-group">
          <label>
            <Door size={13} weight="fill" /> חדר
          </label>
          <select
            value={formData.roomId}
            onChange={(e) => updateField("roomId", e.target.value)}
          >
            <option value="">ללא שיוך לחדר</option>
            {accessibleRooms.map((room) => (
              <option key={room.id} value={room.id}>
                {room.name}
              </option>
            ))}
          </select>
          {roomCheckPending && (
            <div className="apt-wiz-rate-hint">בודק זמינות חדר…</div>
          )}
          {!roomCheckPending &&
            (roomConflicts.length > 0 || roomAvailabilityError) && (
              <RoomBlockedPanel form={form} />
            )}

          {/* Series edit: warn (non-blocking) about future occurrences that
              clash with the new room/hour */}
          {form.isEditing && seriesEditRoomWarnings.length > 0 && (
            <div className="apt-wiz-gate-card">
              <div className="apt-wiz-gate-text">
                <Warning size={15} weight="fill" />
                <span>
                  {seriesEditRoomWarnings.length === 1
                    ? `מפגש עתידי אחד בסדרה מתנגש עם החדר (${formatHebrewDate(
                        seriesEditRoomWarnings[0].startTime,
                        "dd/MM"
                      )} ${formatTime(seriesEditRoomWarnings[0].startTime)})`
                    : `${seriesEditRoomWarnings.length} מפגשים עתידיים בסדרה מתנגשים עם החדר`}
                  {" — השינוי יישמר, מומלץ לבדוק את היומן"}
                </span>
              </div>
            </div>
          )}

          {/* Per-meeting room selection (series create only) */}
          {formData.isRecurring &&
            !form.isEditing &&
            seriesPreview.items.length > 0 && (
              <>
                <button
                  type="button"
                  className="apt-wiz-sms-details-toggle"
                  onClick={() => setShowPerMeetingRooms((v) => !v)}
                >
                  <DoorOpen size={13} />
                  {showPerMeetingRooms
                    ? "הסתרת בחירת חדר לכל מפגש"
                    : "בחירת חדר לכל מפגש"}
                </button>
                {seriesRoomCheckPending && (
                  <div className="apt-wiz-rate-hint">בודק זמינות חדרים לכל המפגשים…</div>
                )}
                {unresolvedSeriesConflicts.length > 0 && (
                  <div className="apt-wiz-gate-card">
                    <div className="apt-wiz-gate-text">
                      <Warning size={15} weight="fill" />
                      <span>
                        {(() => {
                          const anyClosed = unresolvedSeriesConflicts.some(
                            (c) => c.cause && c.cause !== "taken"
                          );
                          const verb = anyClosed ? "החדר תפוס או סגור" : "החדר תפוס";
                          return unresolvedSeriesConflicts.length === 1
                            ? `באחד המפגשים ${verb} — ניתן לבחור חדר אחר, לדלג על המפגש או לקבוע ללא חדר`
                            : `ב-${unresolvedSeriesConflicts.length} מפגשים ${verb} — ניתן לבחור חדר אחר, לדלג עליהם או לקבוע ללא חדר`;
                        })()}
                      </span>
                    </div>
                  </div>
                )}
                {showPerMeetingRooms && (
                  <div className="apt-wiz-occurrence-list">
                    {seriesPreview.items.map((item, idx) => {
                      const override = seriesOverrides[idx];
                      const selectValue =
                        override?.roomId === undefined
                          ? ROOM_DEFAULT
                          : override.roomId || "";
                      const baseRoom = accessibleRooms.find(
                        (r) => r.id === formData.roomId
                      );
                      const conflict = seriesRoomConflicts.find(
                        (c) => c.idx === idx
                      );
                      const rowUnassigned = seriesUnassignedIdx.includes(idx);
                      return (
                        <div
                          key={idx}
                          className={`apt-wiz-occurrence-row ${conflict || rowUnassigned ? "has-conflict" : ""}`}
                        >
                          <div className="apt-wiz-occurrence-main">
                            <span className="apt-wiz-occurrence-num">
                              {item.seriesIndex}
                            </span>
                            <span className="apt-wiz-occurrence-day">
                              יום {HEBREW_DAYS_FULL[item.date.getDay()]}{" "}
                              {formatHebrewDate(item.date, "dd/MM")} · {item.time}
                            </span>
                            <select
                              className="apt-wiz-occurrence-room-select"
                              value={selectValue}
                              onChange={(e) => {
                                const val = e.target.value;
                                setSeriesOverrides((prev) => {
                                  const next = { ...prev };
                                  const row = { ...next[idx] };
                                  if (val === ROOM_DEFAULT) {
                                    delete row.roomId;
                                  } else {
                                    row.roomId = val; // "" = no room
                                  }
                                  if (Object.keys(row).length === 0) {
                                    delete next[idx];
                                  } else {
                                    next[idx] = row;
                                  }
                                  return next;
                                });
                              }}
                            >
                              <option value={ROOM_DEFAULT}>
                                {baseRoom
                                  ? `חדר ברירת מחדל (${baseRoom.name})`
                                  : "כמו ההגדרה הכללית"}
                              </option>
                              <option value="">ללא חדר</option>
                              {accessibleRooms.map((room) => (
                                <option key={room.id} value={room.id}>
                                  {room.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          {override?.skip === true ? (
                            <div className="apt-wiz-occurrence-holiday">
                              <Info size={12} weight="fill" />
                              <span>המפגש ידולג ולא ייקבע</span>
                              <button
                                type="button"
                                className="apt-wiz-occurrence-action-btn"
                                onClick={() =>
                                  setSeriesOverrides((prev) => {
                                    const next = { ...prev };
                                    const row = { ...next[idx] };
                                    delete row.skip;
                                    if (Object.keys(row).length === 0) delete next[idx];
                                    else next[idx] = row;
                                    return next;
                                  })
                                }
                              >
                                ביטול דילוג
                              </button>
                            </div>
                          ) : conflict && isAdmin && override?.override === true ? (
                            <div className="apt-wiz-occurrence-holiday">
                              <Info size={12} weight="fill" />
                              <span>ייקבע למרות החסימה (אישור מנהל)</span>
                              <button
                                type="button"
                                className="apt-wiz-occurrence-action-btn"
                                onClick={() =>
                                  setSeriesOverrides((prev) => {
                                    const next = { ...prev };
                                    const row = { ...next[idx] };
                                    delete row.override;
                                    if (Object.keys(row).length === 0) delete next[idx];
                                    else next[idx] = row;
                                    return next;
                                  })
                                }
                              >
                                ביטול
                              </button>
                            </div>
                          ) : conflict ? (
                            <div className="apt-wiz-occurrence-holiday danger">
                              <Warning size={12} weight="fill" />
                              <span>
                                {conflict.message}
                                {conflict.conflictingPatientName
                                  ? ` (${conflict.conflictingPatientName})`
                                  : ""}
                              </span>
                              <span className="apt-wiz-occurrence-actions">
                                <button
                                  type="button"
                                  className="apt-wiz-occurrence-action-btn"
                                  onClick={() =>
                                    setSeriesOverrides((prev) => ({
                                      ...prev,
                                      [idx]: { ...prev[idx], skip: true },
                                    }))
                                  }
                                >
                                  דלג על מפגש זה
                                </button>
                                {isAdmin && (
                                  <button
                                    type="button"
                                    className="apt-wiz-occurrence-action-btn"
                                    onClick={() =>
                                      setSeriesOverrides((prev) => ({
                                        ...prev,
                                        [idx]: { ...prev[idx], override: true },
                                      }))
                                    }
                                  >
                                    קבע בכל זאת
                                  </button>
                                )}
                              </span>
                            </div>
                          ) : null}
                          {rowUnassigned && override?.skip !== true && (
                            <div className="apt-wiz-occurrence-holiday danger">
                              <Warning size={12} weight="fill" />
                              <span>המטפל/ת אינו/ה משויך/ת למטופל/ת</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
        </div>
      )}

      {showNoRoomGate && (
        <div className={`apt-wiz-gate-card ${noRoomConfirmed ? "confirmed" : ""}`}>
          <div className="apt-wiz-gate-text">
            <Warning size={15} weight="fill" />
            <span>הפגישה תיקבע ללא שיוך לחדר.</span>
          </div>
          {!noRoomConfirmed ? (
            <button
              type="button"
              className="apt-wiz-gate-btn"
              onClick={() => setNoRoomConfirmed(true)}
            >
              אישור
            </button>
          ) : (
            <span className="apt-wiz-gate-ok">אושר</span>
          )}
        </div>
      )}
    </div>
  );
}

export default DetailsStep;
